import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Menu, MenuItem, ListItemIcon, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import JobService from '../../services/JobService';
import { errorMessage } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import ConfirmDialog from '../common/ConfirmDialog';
import { SectionCard } from '../common/ui';
import { fmtDayLong, parseLocalDate, todayIso } from '../../utils/format';
import { ACCEPT_ANY, ACCEPT_IMAGE, PdfRow, PhotoThumb, useFilePicker } from './files';

function VisitDialog({ open, visit, onClose, onSave }) {
    const { t } = useTranslation();
    const [date, setDate] = useState(todayIso());
    const [description, setDescription] = useState('');
    const [photos, setPhotos] = useState([]);
    const [saving, setSaving] = useState(false);
    const [photoInput, pickPhotos] = useFilePicker((fl) => setPhotos(p => [...p, ...Array.from(fl)]), { accept: ACCEPT_IMAGE });
    const [cameraInput, takePhoto] = useFilePicker((fl) => setPhotos(p => [...p, ...Array.from(fl)]), { accept: ACCEPT_IMAGE, capture: true });

    useEffect(() => {
        if (!open) return;
        setDate(visit?.visitDate || todayIso());
        setDescription(visit?.description || '');
        setPhotos([]);
        setSaving(false);
    }, [open, visit]);

    const save = async () => {
        setSaving(true);
        const ok = await onSave({ visitDate: date, description }, photos);
        if (!ok) setSaving(false);
    };

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700 }}>{visit ? t('visits.edit') : t('visits.add')}</DialogTitle>
            <DialogContent>
                {photoInput}{cameraInput}
                <TextField type="date" size="small" label={t('visits.date')} value={date} onChange={(e) => setDate(e.target.value)}
                           slotProps={{ inputLabel: { shrink: true } }} sx={{ mt: 1, mb: 2, width: 200 }} required />
                <TextField fullWidth multiline minRows={4} label={t('visits.description')} value={description}
                           onChange={(e) => setDescription(e.target.value)} />
                {!visit && (
                    <Box sx={{ mt: 2 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475569', mb: 1 }}>{t('visits.photosOptional')}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Button size="small" variant="outlined" startIcon={<PhotoCameraOutlinedIcon />} onClick={takePhoto}>{t('files.takePhoto')}</Button>
                            <Button size="small" startIcon={<AttachFileIcon />} onClick={pickPhotos}>{t('files.addFile')}</Button>
                            {photos.length > 0 && <Typography sx={{ fontSize: 12.5, color: '#64748B' }}>{t('files.photos')}: {photos.length}</Typography>}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={saving} sx={{ color: '#64748B' }}>{t('common.cancel')}</Button>
                <Button variant="contained" disableElevation onClick={save} disabled={saving || !date} sx={{ minWidth: 100 }}>
                    {saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function VisitItem({ job, visit, files, upload, uploading, openFile, askDelete, onEdit, onDelete, readOnly }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [anchor, setAnchor] = useState(null);
    const [attachInput, attach] = useFilePicker((fl) => upload(fl, { visitId: visit.id }), { accept: ACCEPT_ANY });
    const photos = files.filter(f => f.kind === 'IMAGE');
    const pdfs = files.filter(f => f.kind !== 'IMAGE');   // PDFs and drawings

    return (
        <Box sx={{ py: 1.75, borderBottom: '1px solid #F5F7FA', '&:last-of-type': { borderBottom: 0 } }}>
            {attachInput}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '10px', bgcolor: 'rgba(16,185,129,0.10)', color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <EventOutlinedIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: '#1a2332' }}>{fmtDayLong(parseLocalDate(visit.visitDate))}</Typography>
                    <Typography sx={{ fontSize: 13, color: visit.description ? '#334155' : '#94A3B8', whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}>
                        {visit.description || t('jobs.noDescription')}
                    </Typography>
                </Box>
                {!readOnly && <Button size="small" startIcon={<PrintOutlinedIcon />} onClick={() => navigate(`/jobs/${job.id}/print/visit/${visit.id}`)}
                        sx={{ display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'nowrap' }}>{t('visits.print')}</Button>}
                {!readOnly && <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label={t('common.more')}><MoreVertIcon fontSize="small" /></IconButton>}
                <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                    <MenuItem onClick={() => { setAnchor(null); navigate(`/jobs/${job.id}/print/visit/${visit.id}`); }} sx={{ fontSize: 14 }}>
                        <ListItemIcon><PrintOutlinedIcon fontSize="small" /></ListItemIcon>{t('visits.print')}
                    </MenuItem>
                    <MenuItem disabled={uploading} onClick={() => { setAnchor(null); attach(); }} sx={{ fontSize: 14 }}>
                        <ListItemIcon><AttachFileIcon fontSize="small" /></ListItemIcon>{t('visits.attachSigned')}
                    </MenuItem>
                    <MenuItem onClick={() => { setAnchor(null); onEdit(); }} sx={{ fontSize: 14 }}>
                        <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>{t('common.edit')}
                    </MenuItem>
                    <MenuItem onClick={() => { setAnchor(null); onDelete(); }} sx={{ fontSize: 14, color: '#DC2626' }}>
                        <ListItemIcon sx={{ color: '#DC2626' }}><DeleteOutlineIcon fontSize="small" /></ListItemIcon>{t('common.delete')}
                    </MenuItem>
                </Menu>
            </Box>
            {(pdfs.length > 0 || photos.length > 0) && (
                <Box sx={{ ml: 5.75, mt: 1 }}>
                    {pdfs.map(f => <PdfRow key={f.id} dense owner={job.id} file={f} onOpen={() => openFile([f], 0)} onDelete={askDelete ? () => askDelete(f) : undefined} />)}
                    {photos.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                            {photos.map((f, i) => <PhotoThumb key={f.id} owner={job.id} file={f} size={72} onOpen={() => openFile(photos, i)} />)}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

/** Site visits of a job (building staking): date, what was done, photos and the signed certificate. */
/** readOnly (employees): visits are listed, but not added, edited, printed or deleted. */
export default function VisitsCard({ job, visits, files, onChanged, upload, uploading, openFile, askDelete, addRequest, readOnly }) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [dialog, setDialog] = useState({ open: false, visit: null });
    const [toDelete, setToDelete] = useState(null);
    const [savingPhotos, setSavingPhotos] = useState(false);

    // The checklist's "Log visit" button opens the dialog here.
    useEffect(() => { if (addRequest) setDialog({ open: true, visit: null }); }, [addRequest]);

    const save = async (payload, photos) => {
        try {
            const v = dialog.visit
                ? await JobService.updateVisit(job.id, dialog.visit.id, payload)
                : await JobService.createVisit(job.id, payload);
            setDialog({ open: false, visit: null });
            showToast(t('visits.saved'), 'success');
            if (photos.length) {
                setSavingPhotos(true);
                await upload(photos, { visitId: v.id });
                setSavingPhotos(false);
            } else {
                onChanged();
            }
            return true;
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
            return false;
        }
    };

    const remove = async () => {
        try {
            await JobService.deleteVisit(job.id, toDelete.id);
            showToast(t('visits.deleted'), 'success');
            onChanged();
        } catch (err) {
            showToast(errorMessage(err, t('common.error')), 'error');
        } finally {
            setToDelete(null);
        }
    };

    return (
        <SectionCard title={t('visits.title')} sx={{ mb: 2.5 }}
                     action={readOnly ? null : <Button size="small" variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => setDialog({ open: true, visit: null })}>{t('visits.add')}</Button>}>
            {(uploading || savingPhotos) && <LinearProgress />}
            <Box sx={{ px: 2.5, py: visits.length ? 0.5 : 0 }}>
                {visits.length ? visits.map(v => (
                    <VisitItem key={v.id} job={job} visit={v} files={files.filter(f => f.visitId === v.id)}
                               upload={upload} uploading={uploading} openFile={openFile} askDelete={askDelete}
                               onEdit={() => setDialog({ open: true, visit: v })} onDelete={() => setToDelete(v)} readOnly={readOnly} />
                )) : (
                    <Typography sx={{ py: 3, fontSize: 13.5, color: '#94A3B8', textAlign: 'center' }}>{t('visits.empty')}</Typography>
                )}
            </Box>
            <Typography sx={{ px: 2.5, pb: 2, fontSize: 12, color: '#94A3B8' }}>{t('visits.signedHint')}</Typography>
            <VisitDialog open={dialog.open} visit={dialog.visit} onClose={() => setDialog({ open: false, visit: null })} onSave={save} />
            <ConfirmDialog open={!!toDelete} title={t('visits.deleteTitle')} text={t('visits.deleteText')}
                           onConfirm={remove} onClose={() => setToDelete(null)} />
        </SectionCard>
    );
}
